import { useQueryClient } from "@tanstack/react-query";
import { Screen, useNavigate, useParams, useStep } from "flemo";
import { PlayIcon, SparklesIcon } from "lucide-react";
import { motion } from "motion/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { AuthGuard } from "@/components/AuthGuard";
import { PaywallDialog } from "@/components/PaywallDialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useFavoriteIdsQuery, useIsPlusQuery, useToggleFavoriteMutation, useWordSetsQuery } from "@/hooks/queries";
import { trackEvent } from "@/lib/analytics";
import { isNativeApp, nativeBridge } from "@/lib/native";
import { fadeTransition } from "@/lib/transitions";

import { CompletedModal } from "../flashcards/_components/CompletedModal";
import { CardStack } from "./_components/CardStack";
import { CountBadge } from "./_components/CountBadge";
import { FlashcardSkeleton } from "./_components/FlashcardSkeleton";
import { Footer } from "./_components/Footer";
import { Header } from "./_components/Header";
import { OptionsModal } from "./_components/OptionsModal";
import { ResultModal } from "./_components/ResultModal";
import { useFlashcard } from "./_hooks/useFlashcard";

/** 라우트 `id`가 바뀔 때 세션 상태를 초기화하도록 자식에 `key`를 붙인다. */
export function FlashcardPage() {
  const { id } = useParams<"/flashcards/:id">();

  return <FlashcardPageContent key={id} />;
}

/** 플래시카드 학습 UI 전체: 세션 훅·이탈 시 캐시 무효화·본문·모달 */
function FlashcardPageContent() {
  const { popStep, pushStep } = useStep<"/flashcards/:id">();
  const { id, openOptionsModal, openResultModal, openCompletedModal } = useParams<"/flashcards/:id">();
  const { replace, pop } = useNavigate();
  const queryClient = useQueryClient();
  const { data: favoriteIds = [] } = useFavoriteIdsQuery();
  const { mutate: toggleFavorite } = useToggleFavoriteMutation();
  const isFavorited = (id: string) => favoriteIds.includes(id);

  const { data: wordSets } = useWordSetsQuery();
  const { data: isPlus } = useIsPlusQuery();

  const {
    isLoading,
    isError,
    words,
    isFavoritesMode,
    currentIndex,
    currentWord,
    isFlipped,
    swipeDirection,
    settings,
    learningCount,
    knownCount,
    headerProgressNumerator,
    headerProgressDenominator,
    sessionLearningCount,
    sessionKnownCount,
    nextSessionCount,
    isAutoPlay,
    flip,
    swipe,
    undo,
    toggleAutoPlay,
    setSwipeDirection,
    speakWord,
    restartWithLearning,
    continueWithNextSession,
    restartAll,
    updateSettings,
  } = useFlashcard();

  const [paywallOpen, setPaywallOpen] = useState(false);
  const [autoplayUnlockDialogOpen, setAutoplayUnlockDialogOpen] = useState(false);
  const [isAutoPlayUnlockedByAd, setIsAutoPlayUnlockedByAd] = useState(false);
  const [isLoadingRewarded, setIsLoadingRewarded] = useState(false);

  const canUseAutoPlay = isPlus || isAutoPlayUnlockedByAd;

  const handleToggleAutoPlay = useCallback(() => {
    if (canUseAutoPlay) {
      trackEvent("autoplay_toggled", { enabled: !isAutoPlay });
      toggleAutoPlay();
    } else if (isNativeApp) {
      // 네이티브 앱: 보상형 광고 or 구독 선택 다이얼로그
      trackEvent("premium_unlock_prompt_opened", { source: "autoplay" });
      setAutoplayUnlockDialogOpen(true);
    } else {
      // 웹 단독: 구독만 안내
      trackEvent("paywall_triggered", { source: "autoplay" });
      setPaywallOpen(true);
    }
  }, [canUseAutoPlay, isAutoPlay, toggleAutoPlay]);

  const handleWatchAdForAutoPlay = useCallback(async () => {
    setIsLoadingRewarded(true);
    trackEvent("ad_rewarded_initiated", { source: "autoplay_unlock" });

    try {
      const result = await nativeBridge.ad.showRewarded();

      if (result.rewarded) {
        setIsAutoPlayUnlockedByAd(true);
        setAutoplayUnlockDialogOpen(false);
        toggleAutoPlay();
        trackEvent("autoplay_toggled", { enabled: true, unlocked_by: "rewarded_ad" });
        toast.success("이번 세션 동안 오토플레이를 사용할 수 있어요!", {
          position: "top-center",
        });
      } else {
        toast.error("광고 시청이 완료되지 않았어요.", {
          position: "top-center",
          richColors: true,
        });
      }
    } catch {
      toast.error("광고를 불러올 수 없어요. 잠시 후 다시 시도해주세요.", {
        position: "top-center",
        richColors: true,
      });
    } finally {
      setIsLoadingRewarded(false);
    }
  }, [toggleAutoPlay]);

  const isLocked = useMemo(() => {
    if (isPlus) {
      return false;
    }

    if (isFavoritesMode) {
      return true;
    }

    const wordSet = wordSets?.find(ws => ws.id === id);

    return wordSet ? !wordSet.is_free : false;
  }, [isPlus, isFavoritesMode, wordSets, id]);

  const handleReturnToFlashcardsList = () => {
    popStep();
    setTimeout(() => replace("/flashcards", {}, { transitionName: fadeTransition.name }), 100);
  };

  const handleGoToTests = () => {
    popStep();
    setTimeout(() => replace("/tests", {}, { transitionName: fadeTransition.name }), 100);
  };

  const handleExit = () => {
    void trackEvent("flashcard_exited", {
      word_set_id: id,
      card_index: currentIndex,
      session_card_count: words.length,
      session_size: settings.sessionSize,
    });
    pop();
  };

  const lockedRedirectedRef = useRef(false);

  // 존재하지 않는 세트·권한 없는 유료 세트·빈 즐겨찾기 직접 접근 시 목록으로 리다이렉트
  useEffect(() => {
    if (isError || isLocked) {
      if (isLocked && !lockedRedirectedRef.current) {
        lockedRedirectedRef.current = true;
        toast.error("접근할 수 없는 학습 세트입니다.", {
          position: "top-center",
          richColors: true,
        });
      }

      replace("/flashcards", {}, { transitionName: "none" });
      return;
    }

    if (!isLoading && isFavoritesMode && words.length === 0) {
      replace("/flashcards", {}, { transitionName: "none" });
    }
  }, [isError, isLocked, isLoading, isFavoritesMode, words.length, replace]);

  // 학습 화면 이탈 시 관련 캐시 갱신 (즐겨찾기 변경 대비 favoriteWords 포함)
  useEffect(() => {
    return () => {
      queryClient.invalidateQueries({ queryKey: ["learnedCounts"] });
      queryClient.invalidateQueries({ queryKey: ["studySummary"] });
      queryClient.removeQueries({ queryKey: ["flashcardWords"] });
      queryClient.invalidateQueries({ queryKey: ["words", "favorites"] });
    };
  }, [queryClient]);

  return (
    <AuthGuard>
      <Screen>
        <main className="h-dvh overflow-hidden bg-primary-background pt-safe pb-safe sm:bg-tertiary sm:flex sm:items-center sm:justify-center">
          {isLoading || isLocked ? (
            <FlashcardSkeleton />
          ) : (
            <Card className="flex w-full h-full min-h-0 flex-col overflow-hidden bg-primary-background pt-2 sm:pt-5 sm:mx-auto sm:max-w-sm sm:rounded-2xl sm:h-fit sm:shadow-lg">
              {/* 헤더 */}
              <Header
                headerProgressNumerator={headerProgressNumerator}
                headerProgressDenominator={headerProgressDenominator}
                onBack={handleExit}
                onOptions={() => pushStep({ id, openOptionsModal: true })}
              />

              {/* 카운트 배지 (즐겨찾기 모드에서는 숨김) */}
              {!isFavoritesMode && (
                <motion.div
                  animate={{
                    opacity: settings.trackCategories ? 1 : 0,
                    display: settings.trackCategories ? "block" : "none",
                  }}
                  transition={{ duration: 0.2 }}
                >
                  <CountBadge learningCount={learningCount} knownCount={knownCount} swipeDirection={swipeDirection} />
                </motion.div>
              )}

              {/* 카드 스택 */}
              <CardStack
                words={words}
                currentIndex={currentIndex}
                isFlipped={isFlipped}
                frontSide={settings.frontSide}
                trackCategories={!isFavoritesMode && settings.trackCategories}
                onFlip={flip}
                onSwipe={swipe}
                onDragUpdate={setSwipeDirection}
                onSpeak={() => {
                  if (currentWord) {
                    speakWord(currentWord.word);
                  }
                }}
                isFavorited={isFavorited}
                onToggleFavorite={toggleFavorite}
              />

              {/* 푸터 */}
              <Footer
                undo={undo}
                isAutoPlay={canUseAutoPlay ? isAutoPlay : false}
                toggleAutoPlay={handleToggleAutoPlay}
                currentIndex={currentIndex}
              />
            </Card>
          )}
        </main>

        {/* 결과 모달 */}
        <ResultModal
          open={openResultModal ?? false}
          onClose={handleReturnToFlashcardsList}
          learningCount={sessionLearningCount}
          knownCount={sessionKnownCount}
          isFavoritesMode={isFavoritesMode}
          favoriteCount={isFavoritesMode ? favoriteIds.length : undefined}
          onRestartWithLearning={restartWithLearning}
          onRestartAll={restartAll}
          onGoToTests={handleGoToTests}
          nextSessionCount={nextSessionCount}
          onContinue={continueWithNextSession}
        />

        {/* 학습 덱을 모두 본 뒤 빈 상태 안내 */}
        <CompletedModal
          open={openCompletedModal ?? false}
          onClose={handleReturnToFlashcardsList}
          onRestartAll={restartAll}
          onGoToTests={handleGoToTests}
        />

        {/* 설정 모달 */}
        <OptionsModal
          open={openOptionsModal ?? false}
          onClose={popStep}
          settings={settings}
          onUpdate={updateSettings}
          onRestart={restartAll}
        />

        {/* 오토플레이 Plus 페이월 */}
        <PaywallDialog open={paywallOpen} onOpenChange={setPaywallOpen} source="autoplay" />

        {/* 오토플레이 잠금 해제 선택 다이얼로그 (네이티브 앱 전용) */}
        <Dialog open={autoplayUnlockDialogOpen} onOpenChange={setAutoplayUnlockDialogOpen}>
          <DialogContent className="max-w-xs rounded-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-center gap-2">
                <PlayIcon className="size-5" />
                오토플레이
              </DialogTitle>
              <DialogDescription className="text-center">
                오토플레이는 Plus 기능이에요.
                <br />
                광고를 보면 이번 세션 동안 사용할 수 있어요.
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-2 pt-2">
              <Button onClick={handleWatchAdForAutoPlay} disabled={isLoadingRewarded} className="w-full gap-2">
                <PlayIcon className="size-4" />
                {isLoadingRewarded ? "광고 불러오는 중..." : "광고 보고 오토플레이 켜기"}
              </Button>

              <Button
                variant="outline"
                className="w-full gap-2"
                onClick={() => {
                  setAutoplayUnlockDialogOpen(false);
                  trackEvent("paywall_triggered", { source: "autoplay" });
                  setPaywallOpen(true);
                }}
              >
                <SparklesIcon className="size-4" />
                Plus 구독하기
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </Screen>
    </AuthGuard>
  );
}
